import React, { createContext, useContext, useMemo, useState } from "react";

// ============================================================
// FYUNOR — Locale & Currency Context
//
// The localisation foundation. No values are hardcoded inside
// calculators; every calculator reads country, currency, and
// number/date formats from here. Future tax/pension/mortgage
// rule packs attach to the same descriptor.
// ============================================================

import { buildLocale } from "@/lib/format";

const DEFAULT = buildLocale();

const LocaleContext = createContext({
  locale: DEFAULT,
  setLocale: () => {},
});

// A small registry of locale presets — calculators never import these
// directly; the context (or a future Country Selector) supplies them.
export const LOCALE_PRESETS = {
  "en-IE": buildLocale({ code: "en-IE", country: "IE", countryName: "Ireland", currency: "EUR", currencySymbol: "€", decimalSeparator: ".", thousandSeparator: ",", dateFormat: "DD/MM/YYYY" }),
  "en-US": buildLocale({ code: "en-US", country: "US", countryName: "United States", currency: "USD", currencySymbol: "$", decimalSeparator: ".", thousandSeparator: ",", dateFormat: "MM/DD/YYYY" }),
  "en-GB": buildLocale({ code: "en-GB", country: "GB", countryName: "United Kingdom", currency: "GBP", currencySymbol: "£", decimalSeparator: ".", thousandSeparator: ",", dateFormat: "DD/MM/YYYY" }),
  "de-DE": buildLocale({ code: "de-DE", country: "DE", countryName: "Germany", currency: "EUR", currencySymbol: "€", decimalSeparator: ",", thousandSeparator: ".", currencyPosition: "suffix", dateFormat: "DD.MM.YYYY" }),
  "ja-JP": buildLocale({ code: "ja-JP", country: "JP", countryName: "Japan", currency: "JPY", currencySymbol: "¥", decimalSeparator: ".", thousandSeparator: ",", dateFormat: "YYYY/MM/DD" }),
};

export function LocaleProvider({ children, initialLocale = LOCALE_PRESETS["en-IE"] }) {
  const [locale, setLocaleState] = useState(initialLocale);

  const value = useMemo(
    () => ({
      locale,
      setLocale: (next) => setLocaleState(typeof next === "function" ? next(locale) : next),
    }),
    [locale]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}